import { useUserMe } from 'shared/api/userMeQuery'
import { useMemo } from 'react'
import { getTenantNameFromAddressbar, getTenantStorageUrl } from 'shared/utils/url'
import { getHelpCenter, getServiceName, isAgencyTenant } from 'shared/utils/tenant'

export interface TenantBranding {
  tenantName: string | undefined
  serviceName: string
  logoSrc: string
  logoAlt: string
  /** デフォルトロゴ (Figma 通りの 12.7px) と代理店ロゴ (32px) で異なる */
  logoHeight: number
  /** 数値固定 or 'auto' (代理店ロゴはアスペクト比維持) */
  logoWidth: number | 'auto'
  /** 'auto' 時の上限幅 */
  logoMaxWidth?: number
  helpCenterUrl: string
  helpCenterLabel: string
  showAppLinks: boolean
}

const DEFAULT_LOGO_SRC = '/assets/logos/jizaipad-logo.svg'

/**
 * 代理店ブランディング（ロゴ差し替え）の対象テナント。
 *
 * VRT の mocks（`.storybook/mocks/handlers/tenantBranding.ts`）が
 * 捕捉すべき外部ストレージのオリジンをここから導出するため export している。
 * 二重管理にするとテナント追加時に片方だけ直し忘れる。
 */
export const BRANDED_TENANTS = new Set(['aktio', 'sooki', 'kanamoto'])

// 旧UI (FooterLogoIcon / HeaderLogoIcon) と同じ src を踏襲する。
// sooki は白カード・白ヘッダーの上に置くので黒ロゴだけを使う。
// 以前あった `/login` 用の白ロゴ分岐は、通常ログイン画面が sooki でロゴを出さず
// テキストだけを出すため、到達しないデッドコードだった（#2293 で削除）。
const resolveLogoSrc = (tenantName: string | undefined): string => {
  switch (tenantName) {
    case 'aktio':
      return '/images/logo_aktio.png'
    case 'sooki':
      return `${getTenantStorageUrl(tenantName)}/public/sooki_logo_black.png`
    case 'kanamoto':
      return `${getTenantStorageUrl(tenantName)}/public/kanareco_logo_blue.png`
    default:
      return DEFAULT_LOGO_SRC
  }
}

// ロゴの代替テキストは、画像に書かれている名前（ワードマーク）にする。
// サービス名（`getServiceName`）とは一致しない（aktio のロゴは「AKTIO」で「AktioEyes」ではない）。
// ブランド名なので翻訳しない。
const resolveLogoAlt = (tenantName: string | undefined): string => {
  switch (tenantName) {
    case 'aktio':
      return 'AKTIO'
    case 'sooki':
      return 'SOOKI'
    case 'kanamoto':
      return 'kanaReco'
    default:
      return 'JIZAIPAD'
  }
}

/**
 * テナント別ブランディング情報を返す hook。
 *
 * テナント判定はハイブリッド方式:
 * 1. ログイン後は userMe.tenants[0].nameAlphabet を優先（API 由来で堅い）
 * 2. ログイン前 / userMe 未取得時は URL サブドメインから取得
 *
 * 旧UI の HeaderLogoIcon / FooterLogoIcon / FooterLink / HelpCenterLink /
 * getServiceName をこの 1 つの hook に集約する。認証画面（LoginView / AuthLayout /
 * useLoginStyle / SSO ログイン）のテナント判定もこの hook を通す。
 * 認証画面では userMe のキャッシュが空なので、URL サブドメインの判定になる。
 */
export function useTenantBranding (): TenantBranding {
  const userMe = useUserMe()

  return useMemo(() => {
    const tenantFromUser = userMe?.tenants?.[0]?.nameAlphabet
    const tenantFromUrl = getTenantNameFromAddressbar(window.location.href)
    const tenantName = (tenantFromUser !== undefined && tenantFromUser !== '')
      ? tenantFromUser
      : tenantFromUrl

    const helpCenter = getHelpCenter(tenantName)
    const isBranded = tenantName !== undefined && BRANDED_TENANTS.has(tenantName)

    return {
      tenantName,
      serviceName: getServiceName(tenantName),
      logoSrc: resolveLogoSrc(tenantName),
      logoAlt: resolveLogoAlt(tenantName),
      logoHeight: isBranded ? 32 : 12.7,
      logoWidth: isBranded ? 'auto' : 91,
      logoMaxWidth: isBranded ? 160 : undefined,
      helpCenterUrl: helpCenter.url,
      helpCenterLabel: helpCenter.label,
      showAppLinks: !isAgencyTenant(userMe?.tenants?.[0])
    }
  }, [userMe])
}
